import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiCalendar, FiMapPin, FiClock, FiUsers, FiShield, FiCheck, FiStar, FiZap, FiTarget, FiTrendingUp, FiAward, FiMenu, FiX } = FiIcons;

function App() {
  const [isRegistered, setIsRegistered] = useState(false);
  const [timeLeft, setTimeLeft] = useState({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, 50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);

  // Countdown timer
  useEffect(() => {
    const eventDate = new Date('2025-11-27T09:00:00');
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = eventDate - now;
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const sponsors = [
    { 
      name: 'Banta',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972378598-5e3e24774_banta.png'
    },
    { 
      name: 'Collaborative Code',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972421468-6c83eaadd_Collaborative_Code.png'
    },
    { 
      name: 'ALVINX',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972442643-7cc8d6166_Alvinx.png'
    },
    { 
      name: 'Gauthier Consulting',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972448483-42210cba2_gauthier.png'
    },
    { 
      name: 'Y&T Designs',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972453748-a4062f769_YTLogo1.png'
    },
    { 
      name: 'Tamrose',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972783533-ab726b551_Tamrose-images.png'
    },
    { 
      name: 'Noble Africa Safaris',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972790066-blob'
    },
    { 
      name: 'HoopDreams Nigeria',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972801030-cd73c20ba_download.jpeg'
    },
    { 
      name: 'Miva',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972807844-Miva-Logo-White-Vertical-1.png'
    },
    { 
      name: 'Echo CTF',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759972829475-Echo-redogo.png'
    },
    { 
      name: 'OSM Thome',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759973768224-ca84b0158_OSM_Thome.png'
    },
    { 
      name: 'Jobred',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759973780163-ee47228fc_jobred.jpg'
    },
    { 
      name: 'Regent Construction',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759973801464-Regent_Construction1.png'
    },
    { 
      name: 'Smart Africa',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759973813786-Smart_Africa_Secretariat_logo.png'
    },
    { 
      name: 'Suwebatu',
      logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759973829487-suwebatu%20logo%201.png'
    }
  ];

  const features = [
    {
      icon: FiUsers,
      title: 'Elite Networking',
      description: 'Connect with cybersecurity professionals, innovators, and industry leaders shaping Africa\'s digital future',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: FiZap,
      title: 'Cutting-Edge Innovation',
      description: 'Experience live demos of AI-powered security solutions and next-generation defensive technologies',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: FiTarget,
      title: 'Real-World Impact',
      description: 'Witness how African tech talent is solving continental challenges with groundbreaking cybersecurity solutions',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const navigationItems = [
    { name: 'Event Details', href: '#hero' },
    { name: 'Organizers', href: '#organizers' },
    { name: 'Sponsors', href: '#sponsors' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      {/* Navigation Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/20 backdrop-blur-xl border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="flex items-center space-x-3"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759975087164-AfriHackBox-Clean-Together2.png"
                alt="AfriHackBox"
                className="h-12 w-auto"
              />
              <div className="hidden md:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent">
                  AfriHackBox 2025
                </h1>
                <p className="text-xs text-slate-400">Kigali, Rwanda</p>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  whileHover={{ scale: 1.05 }}
                  className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector(item.href)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {item.name}
                </motion.a>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="md:hidden text-white p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </motion.button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-white/10"
            >
              <nav className="flex flex-col space-y-4">
                {navigationItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-slate-300 hover:text-cyan-400 transition-colors duration-300 font-medium py-2"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMenuOpen(false);
                      document.querySelector(item.href)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    {item.name}
                  </a>
                ))}
              </nav>
            </motion.div>
          )}
        </div>
      </motion.header>

      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400/30 rounded-full"
            animate={{
              x: [0, 100, 0],
              y: [0, -100, 0],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 5
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <motion.div 
            style={{ y: y1 }}
            className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"
          />
          <motion.div 
            style={{ y: y2 }}
            className="absolute inset-0 bg-dots opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-slate-900/50" />
        </div>
        
        {/* Glowing Orbs */}
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl"
        />
        <motion.div 
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl"
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            {/* Logo Display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="mb-8"
            >
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759975087164-AfriHackBox-Clean-Together2.png"
                alt="AfriHackBox"
                className="h-24 md:h-32 lg:h-40 mx-auto drop-shadow-2xl"
              />
            </motion.div>

            {/* Premium Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-red-500/10 via-white/10 to-green-500/10 border border-red-500/30 backdrop-blur-sm mb-8 shadow-lg"
            >
              <SafeIcon icon={FiShield} className="w-5 h-5 text-red-400 mr-3" />
              <span className="text-white font-semibold tracking-wide">Africa's Premier Cybersecurity Summit</span>
              <div className="ml-3 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </motion.div>
            
            {/* Main Title */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="text-4xl md:text-6xl lg:text-7xl font-black mb-8 leading-tight"
            >
              <motion.span 
                className="bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent inline-block"
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                }}
                transition={{ duration: 5, repeat: Infinity }}
                style={{ backgroundSize: '200% 200%' }}
              >
                Hackathon 2025
              </motion.span>
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-xl md:text-2xl text-slate-200 mb-12 max-w-4xl mx-auto leading-relaxed font-light"
            >
              Join Africa's most <span className="text-red-400 font-semibold">dynamic cybersecurity</span> and innovation experience in the vibrant heart of <span className="text-green-400 font-semibold">Kigali</span>
            </motion.p>
            
            {/* Event Details */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              className="flex flex-col lg:flex-row gap-6 justify-center items-center mb-16"
            >
              {[
                { icon: FiCalendar, text: 'Thursday, November 27th, 2025', color: 'text-red-400' },
                { icon: FiClock, text: '9:00 AM - 1:00 PM', color: 'text-white' },
                { icon: FiMapPin, text: 'UBUMWE Grand Hotel, Kigali', color: 'text-green-400' }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center bg-white/5 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10 hover:border-red-400/50 transition-all duration-300"
                >
                  <SafeIcon icon={item.icon} className={`w-5 h-5 mr-3 ${item.color}`} />
                  <span className="text-slate-200 font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Countdown Timer */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/50 to-purple-900/50" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Event <span className="bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent">Countdown</span>
            </h2>
            <p className="text-xl text-slate-300">The future starts in...</p>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Seconds', value: timeLeft.seconds }
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-green-500/20 rounded-2xl blur group-hover:blur-md transition-all duration-300" />
                <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 group-hover:border-red-400/50 transition-all duration-300">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-red-400 to-green-400 bg-clip-text text-transparent mb-3">
                    {item.value || '00'}
                  </div>
                  <div className="text-slate-300 text-sm uppercase tracking-widest font-medium">
                    {item.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Organizers Section */}
      <section id="organizers" className="py-20 bg-gradient-to-r from-black/30 to-purple-900/30 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Event <span className="bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent">Organizers</span>
            </h2>
            <p className="text-xl text-slate-300">Powered by industry leaders</p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759974068076-Ju_logo.png',
                name: 'JUSTUNSECURE',
                gradient: 'from-red-500 to-pink-500'
              },
              {
                logo: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759974077467-suwebatu%20logo%201.png',
                name: 'SUWEBATU',
                gradient: 'from-green-500 to-emerald-500'
              }
            ].map((organizer, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${organizer.gradient} opacity-10 rounded-3xl blur group-hover:opacity-20 transition-all duration-300`} />
                <div className="relative bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-xl rounded-3xl p-10 border border-slate-700/50 group-hover:border-red-400/50 transition-all duration-300 text-center">
                  <div className="w-24 h-24 mx-auto mb-8 bg-white/10 rounded-2xl flex items-center justify-center">
                    <img 
                      src={organizer.logo}
                      alt={organizer.name}
                      className="h-16 max-w-full object-contain"
                      onError={(e) => {e.target.style.display = 'none'}}
                    />
                  </div>
                  <p className={`text-3xl font-bold bg-gradient-to-r ${organizer.gradient} bg-clip-text text-transparent`}>
                    {organizer.name}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section id="sponsors" className="py-20 bg-gradient-to-r from-black/40 to-purple-900/40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent">Sponsors</span>
            </h2>
            <p className="text-xl text-slate-300">Proudly supported by innovative industry partners</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {sponsors.map((sponsor, index) => (
              <motion.div
                key={sponsor.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-green-500/10 rounded-2xl blur group-hover:from-red-500/20 group-hover:to-green-500/20 transition-all duration-300" />
                <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 group-hover:border-red-400/50 transition-all duration-300 flex items-center justify-center h-24">
                  <img 
                    src={sponsor.logo} 
                    alt={sponsor.name}
                    className="h-12 max-w-full object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/120x48/64748b/ffffff?text=${sponsor.name}`;
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Shape Africa's <br />
              <span className="bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent">
                Digital Future
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-slate-300 max-w-5xl mx-auto leading-relaxed">
              This transformative event brings together visionary professionals, groundbreaking innovators, and influential business leaders passionate about revolutionizing Africa's digital security landscape.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-10 rounded-3xl blur group-hover:opacity-20 transition-all duration-300`} />
                <div className="relative bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-xl rounded-3xl p-10 border border-slate-700/50 group-hover:border-red-400/50 transition-all duration-300 h-full">
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300`}>
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-6 group-hover:text-red-400 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed text-lg">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-slate-800/50 bg-gradient-to-r from-slate-900/90 to-black/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-8 flex flex-col items-center">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759975087164-AfriHackBox-Clean-Together2.png"
                alt="AfriHackBox"
                className="h-16 w-auto mb-4"
              />
              <h3 className="text-3xl font-bold bg-gradient-to-r from-red-400 via-white to-green-400 bg-clip-text text-transparent mb-2">
                AfriHackBox 2025
              </h3>
              <p className="text-slate-400 text-lg">
                Shaping the future of African cybersecurity
              </p>
            </div>
            
            <p className="text-slate-400 mb-6 text-lg">
              © 2025 AfriHackBox Hackathon. Organized by <span className="text-red-400 font-semibold">JUSTUNSECURE</span> and <span className="text-green-400 font-semibold">SUWEBATU</span>.
            </p>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-slate-500">
              <span className="flex items-center">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 text-red-400" />
                UBUMWE Grand Hotel, Kigali, Rwanda
              </span>
              <span className="hidden md:block">•</span>
              <span className="flex items-center">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 text-white" />
                November 27th, 2025
              </span>
              <span className="hidden md:block">•</span>
              <span className="flex items-center">
                <SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-green-400" />
                9:00 AM - 1:00 PM
              </span>
            </div>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}

export default App;